<template lang="pug">
#reservation-schedule-template-list(v-cloak)
  .box
    .box-header.d-flex.flex-row
      .titles
        .title
          span 一括設定管理
      .flex-fill.d-flex.flex-row-reverse
        button.btn.add-btn(@click="onBack" type="button") 戻る
        button.btn.add-btn.mr-2(@click="onRegister" type="button") 新規作成
    .box-body
      .note
        span 面会予約の一括設定を登録すると、同じ設定を繰り返し使用することができます。
        br
        span 毎月同じような面会スケジュールを作成されている場合にご使用ください。
      ReservationScheduleTemplateTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onEdit="onEdit"
        @onDelete="onDelete"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        //- 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・設定名
        //- ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
        //- ページネーション
        b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading"
          @change="selectPage" hide-goto-end-buttons)

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteTemplate(deleteTargetId)")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";

import ReservationScheduleTemplatesService, { ReservationScheduleTemplateGetModel } from "@/commons/services/ReservationScheduleTemplates";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import ReservationScheduleTemplateTable, {
  FilterSetting,
  ReservationScheduleTemplateTableField,
  ReservationScheduleTemplateTableItem
} from "@/pc/containers/reservations/ReservationScheduleTemplateTable.vue";

@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    ReservationScheduleTemplateTable
  }
})
export default class ReservationScheduleTemplateList extends Vue {
  /** 面会スケジュールテンプレートサービス */
  @Inject("ReservationScheduleTemplatesService") templateService!: ReservationScheduleTemplatesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** テーブル列名 */
  fields: Array<ReservationScheduleTemplateTableField> = [];
  /** テーブル各行の情報 */
  items: Array<ReservationScheduleTemplateTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** 削除対象のID */
  deleteTargetId: number | null = null;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.reservationScheduleTemplate.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.reservationScheduleTemplate.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `reservation-schedule-template-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    const fields: Array<ReservationScheduleTemplateTableField> = this.createFields();
    this.fields = fields;

    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.reservationScheduleTemplate.perPage);

    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<ReservationScheduleTemplateTableField> {
    const fields: Array<ReservationScheduleTemplateTableField> = [];
    fields.push({ key: "name", label: "設定名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "detail", label: "設定内容", sortable: false, sortDirection: "desc", class: "detail" });
    fields.push({ key: "control", label: "操作", sortable: false, sortDirection: "desc", class: "control text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });
      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["reservation-schedule-template-list-per-page"];
      return isNaN(perPage) ? null : perPage;
    }
    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    this.setRows();

    const templates: Array<ReservationScheduleTemplateGetModel> = await this.getTemplates();
    this.items = templates;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.templateService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 面会予約スケジュールテンプレートを取得する
   */
  public async getTemplates(): Promise<Array<ReservationScheduleTemplateGetModel>> {
    const response = await this.templateService.gets(userStore);
    return response;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 登録画面へ遷移する
   */
  public onRegister(): void {
    this.$router.push(PcUrl.ReservationScheduleTemplateRegister);
    return;
  }

  /**
   * ### 面会予約スケジュール画面へ遷移する
   */
  public onBack(): void {
    this.$router.push(PcUrl.ReservationSchedules);
    return;
  }

  /**
   * ### 編集画面へ遷移する
   */
  public onEdit(item: ReservationScheduleTemplateTableItem): void {
    this.$router.push({ name: PcUrl.ReservationScheduleTemplateEdit, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public onDelete(item: ReservationScheduleTemplateTableItem): void {
    this.deleteTargetId = item.id;
    this.deleteMessage = `${item.name}を削除します。よろしいですか？`;
    this.showDeleteModal = true;
    return;
  }

  /**
   * ### 面会予約テンプレートを削除する
   */
  public async deleteTemplate(id: number): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.templateService.delete(userStore, id);

    // 削除にしたものをitemsから削除することでページのデータの再取得はしない
    this.items = this.items.concat().filter((item) => {
      if (item.id == id) {
        return false;
      } else {
        return true;
      }
    });

    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#reservation-schedule-template-list {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
